import {
  DateTimeText,
  createDateFormatter,
  useDisplayTimezone,
  type TimezoneMode,
} from "@nexus/ui";
import { memo, useEffect, useId, useMemo, useState } from "react";
import {
  BellRing,
  Check,
  CircleAlert,
  ExternalLink,
  FileText,
  ListFilter,
  Search,
  Send,
  ShieldBan,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";
import { Button, ToggleChip } from "@nexus/ui";
import {
  defineLabels,
  resolveLabels,
  StatusChip,
  UpdatingPill,
  Widget,
  WidgetState,
  WidgetFooter,
  MetricValue,
  DetailWorkspace,
  firstError,
  unwrap,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type {
  DrawerCallbacks,
  DrawerDescriptor,
  JsonValue,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
} from "@nexus/widget-sdk";
import type {
  InfringementRecordEntry,
  InfringementRecordsConfig,
  InfringementRecordsData,
} from "./types";
import { formatResponseTargets } from "./format";
import { EmptyDataState } from "@nexus/widget-kit";

export type {
  InfringementRecordEntry,
  InfringementRecordsConfig,
  InfringementResponseEntry,
  InfringementRecordsData,
} from "./types";

export const LABELS = defineLabels([
  {
    key: "noMatchTitle",
    name: "No rows after filtering: title",
    default: "No records match these filters",
  },
  {
    key: "noMatchDescription",
    name: "No rows after filtering: description",
    default: "Clear the filters to see the loaded records.",
  },
  {
    key: "clearFilters",
    name: "Button: Clear filters",
    default: "Clear filters",
  },
  { key: "loadMore", name: "Button: Load more", default: "Load more" },
  {
    key: "kpiShown",
    name: "KPI caption: Infringements shown",
    default: "Infringements shown",
  },
  {
    key: "kpiActionable",
    name: "KPI caption: Actionable, no response",
    default: "Actionable · no response",
  },
  {
    key: "kpiFailed",
    name: "KPI caption: With failed responses",
    default: "With failed responses",
  },
  { key: "colName", name: "Table column: Name", default: "Name" },
  {
    key: "colEngineTenant",
    name: "Table column: Engine / Tenant",
    default: "Engine / Tenant",
  },
  {
    key: "colCrossDomain",
    name: "Table column: Cross Domain",
    default: "Cross Domain",
  },
  {
    key: "colDetections",
    name: "Table column: Detections",
    default: "Detections",
  },
  {
    key: "colResponses",
    name: "Table column: Responses",
    default: "Responses",
  },
  { key: "colStatus", name: "Table column: Status", default: "Status" },
  {
    key: "colFirstSeen",
    name: "Table column: First seen",
    default: "First seen",
  },
  {
    key: "filterInfringementType",
    name: "Filter caption: Infringement type",
    default: "Infringement type",
  },
  {
    key: "filterResponseStatus",
    name: "Filter caption: Response status",
    default: "Response status",
  },
  {
    key: "searchPlaceholder",
    name: "Search box placeholder",
    default: "Search infringements",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "infringement-records",
  name: "Infringement Records",
  description:
    "Infringement detections for the selected protected event, with related response history.",
  version: "1.1.0",
  labels: LABELS,
  dataRequests: {
    records: {
      source: "infringement-records",
      contract: { name: "infringement-records", version: 1 },
      configurable: false,
      filterOutputs: {
        infringementTypes: {
          label: "Infringement types",
          field: "infringement_type",
          operator: "in",
        },
        responseStatuses: {
          label: "Response statuses",
          field: "latest_response_status",
          operator: "in",
        },
        search: {
          label: "Search infringements",
          field: "search",
          operator: "eq",
        },
      },
    },
  },
};

export const defaultConfig: InfringementRecordsConfig = {
  title: "Infringement Records",
  limit: 20,
  infringementTypes: ["theft_for_restreaming", "cdn_leeching", "restreaming", "account_sharing"],
  responseStatuses: ["pending", "confirmed", "failed", "cancelled"],
};

const EMPTY_RECORDS: InfringementRecordEntry[] = [];

const INFRINGEMENT_TYPE_LABELS: Record<string, string> = {
  theft_for_restreaming: "TheftForRestreaming",
  cdn_leeching: "CDNLeeching",
  restreaming: "Restreaming",
  account_sharing: "AccountSharing",
};

// ── Utilities ────────────────────────────────────────────────────────────────

function titleCase(value: string): string {
  return value ? value.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) : "—";
}

function infringementTypeLabel(value: string): string {
  return INFRINGEMENT_TYPE_LABELS[value] ?? titleCase(value);
}

function normalizeInfringementType(type: string): string {
  return type.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function formatDate(value: string | null, timezone: TimezoneMode): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : createDateFormatter(timezone, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
}

function responseTone(status: string): "ok" | "warn" | "error" | "neutral" {
  switch (status.toLowerCase()) {
    case "confirmed":
      return "ok";
    case "pending":
      return "warn";
    case "failed":
    case "cancelled":
      return "error";
    default:
      return "neutral";
  }
}

function normalizeResponseStatus(status: string | null): string {
  return status?.toLowerCase() ?? "";
}

function recordTargets(record: InfringementRecordEntry): string[] {
  const tokens = record.tokens ?? [];
  if (record.ips.length > 0) return record.ips;
  if (record.accounts.length > 0) return record.accounts;
  if (tokens.length > 0) return tokens;
  return record.domains;
}

function originEngineLabel(record: InfringementRecordEntry): string | null {
  if (record.origin_engine_label) return record.origin_engine_label;
  return record.origin_engine_id !== null ? String(record.origin_engine_id) : null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

// A minimal pill-style toggle button used for the filter bar.
function FilterPill({
  label,
  active,
  tone,
  onClick,
}: {
  label: string;
  active: boolean;
  tone?: "ok" | "warn" | "error" | "neutral";
  onClick: () => void;
}) {
  return (
    <ToggleChip pressed={active} tone={tone ?? "accent"} onPressedChange={onClick}>
      {label}
    </ToggleChip>
  );
}

function ResponseStatusIndicator({ status }: { status: string }) {
  const tone = responseTone(status);

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 text-xs font-medium",
        tone === "error"
          ? "text-[var(--status-error)]"
          : tone === "warn"
            ? "text-[var(--status-warn)]"
            : "text-secondary",
      ].join(" ")}
    >
      <span
        className={[
          "h-1.5 w-1.5 shrink-0 rounded-full",
          tone === "error"
            ? "bg-[var(--status-error)]"
            : tone === "warn"
              ? "bg-[var(--status-warn)]"
              : "bg-current opacity-60",
        ].join(" ")}
        aria-hidden
      />
      {titleCase(status)}
    </span>
  );
}

// ── Table row ────────────────────────────────────────────────────────────────

const InfringementRow = memo(function InfringementRow({
  record,
  active,
  interactive,
  onSelect,
}: {
  record: InfringementRecordEntry;
  active: boolean;
  interactive: boolean;
  onSelect: (record: InfringementRecordEntry) => void;
}) {
  const crossDomain = originEngineLabel(record);
  const timezone = useDisplayTimezone();

  return (
    <tr
      onClick={() => interactive && onSelect(record)}
      onKeyDown={(event) => {
        if (!interactive) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(record);
        }
      }}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      aria-current={active ? "true" : undefined}
      aria-label={interactive ? `Open infringement record ${record.infringement_name}` : undefined}
      className={[
        "border-b border-border last:border-b-0 [&>td]:transition-colors [&>td]:duration-150",
        interactive ? "cursor-pointer" : "",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-cyan/45",
        active
          ? "[&>td]:bg-row-selected [&:hover>td]:bg-row-selected-hover"
          : "[&:hover>td]:bg-surface-elevated",
      ].join(" ")}
    >
      <td className="min-w-0 px-3 py-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-medium text-primary" title={record.infringement_name}>
            {record.infringement_name}
          </span>
          <span className="truncate text-xs text-muted" title={record.infringement_type}>
            {infringementTypeLabel(record.infringement_type)} · {record.detection_domain}
          </span>
        </div>
      </td>
      <td className="px-2 py-2 text-secondary">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate" title={record.engine_label}>
            {record.engine_label}
          </span>
          <span className="truncate text-xs text-muted" title={record.tenant_label}>
            {record.tenant_label}
          </span>
        </div>
      </td>
      <td className="px-2 py-2 text-secondary">
        {crossDomain ? (
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate" title={crossDomain}>
              {crossDomain}
            </span>
            <span
              className="inline-flex shrink-0 items-center justify-center rounded-md bg-brand-purple/10 p-1 text-brand-purple"
              title="Cross-domain response required"
              aria-label="Cross-domain response required"
            >
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
            </span>
          </div>
        ) : (
          <span className="text-base leading-none text-muted" aria-label="No cross-domain source">
            —
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-center tabular-nums text-secondary">
        {record.detection_count}
      </td>
      <td className="px-2 py-2 text-center tabular-nums text-secondary">{record.response_count}</td>
      <td className="px-2 py-2">
        {record.latest_response_status ? (
          <ResponseStatusIndicator status={record.latest_response_status} />
        ) : (
          <span className="text-base leading-none text-muted" aria-label="No response status">
            —
          </span>
        )}
      </td>
      <td className="py-2 pl-2 pr-[1.625rem] text-right text-xs tabular-nums text-muted">
        {formatDate(record.first_detection, timezone)}
      </td>
    </tr>
  );
});

// ── Filter bar ───────────────────────────────────────────────────────────────

function getDashboardFilterValues(
  filters: WidgetProps<InfringementRecordsConfig>["dashboard"]["filters"],
  field: string,
) {
  const values = filters.dataFilters
    .filter((filter) => filter.field === field)
    .flatMap((filter) => (Array.isArray(filter.value) ? filter.value : [filter.value]))
    .filter((value): value is string => typeof value === "string");

  return new Set(values);
}

function HeaderControls({
  filtersExpanded,
  filterPanelId,
  onFiltersExpandedChange,
  typeOptions,
  statusOptions,
  activeTypes,
  activeStatuses,
  searchValue,
  onSearch,
  searchPlaceholder,
}: {
  filtersExpanded: boolean;
  filterPanelId: string;
  onFiltersExpandedChange: (expanded: boolean) => void;
  typeOptions: string[];
  statusOptions: string[];
  activeTypes: Set<string>;
  activeStatuses: Set<string>;
  searchValue: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
}) {
  const hasFilters = activeTypes.size > 0 || activeStatuses.size > 0;
  const activeFilterCount = activeTypes.size + activeStatuses.size;
  const [searchExpanded, setSearchExpanded] = useState(searchValue !== "");
  const [searchDraft, setSearchDraft] = useState(searchValue);

  useEffect(() => setSearchDraft(searchValue), [searchValue]);

  const hasFilterOptions = typeOptions.length > 0 || statusOptions.length > 0;

  return (
    <div className="flex items-center gap-1">
      {searchExpanded ? (
        <form
          className="relative w-36 @2xl/widget:w-52"
          onSubmit={(event) => {
            event.preventDefault();
            onSearch(searchDraft.trim());
          }}
        >
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted"
            aria-hidden
          />
          <input
            autoFocus
            type="search"
            value={searchDraft}
            onChange={(event) => {
              const value = event.target.value;
              setSearchDraft(value);
              if (value === "") onSearch("");
            }}
            onBlur={() => {
              if (searchDraft.trim() === "") setSearchExpanded(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape" && searchDraft === "") setSearchExpanded(false);
            }}
            placeholder={searchPlaceholder}
            aria-label="Search infringements"
            className="h-7 w-full rounded border border-border bg-surface pl-7 pr-2 text-xs text-primary placeholder:text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          />
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setSearchExpanded(true)}
          aria-label="Search infringements"
          title="Search"
          className={[
            "inline-flex h-7 w-7 items-center justify-center rounded text-muted transition-colors hover:bg-surface-elevated hover:text-primary",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
            searchValue ? "bg-brand-cyan/10 text-brand-cyan" : "",
          ].join(" ")}
        >
          <Search className="h-4 w-4" aria-hidden />
        </button>
      )}
      {hasFilterOptions && (
        <button
          type="button"
          onClick={() => onFiltersExpandedChange(!filtersExpanded)}
          aria-expanded={filtersExpanded}
          aria-controls={filterPanelId}
          aria-label="Toggle filters"
          title="Filters"
          className={[
            "relative inline-flex h-7 w-7 items-center justify-center rounded text-muted transition-colors hover:bg-surface-elevated hover:text-primary",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
            filtersExpanded || hasFilters ? "bg-brand-cyan/10 text-brand-cyan" : "",
          ].join(" ")}
        >
          <ListFilter className="h-4 w-4" aria-hidden />
          {activeFilterCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-brand-cyan px-1 text-xs font-semibold leading-none text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

function FilterRow({
  typeOptions,
  statusOptions,
  activeTypes,
  activeStatuses,
  onToggleType,
  onToggleStatus,
  onClear,
  captions,
}: {
  typeOptions: string[];
  statusOptions: string[];
  activeTypes: Set<string>;
  activeStatuses: Set<string>;
  onToggleType: (type: string) => void;
  onToggleStatus: (status: string) => void;
  onClear: () => void;
  captions: { infringementType: string; responseStatus: string };
}) {
  const hasFilters = activeTypes.size > 0 || activeStatuses.size > 0;

  return (
    <div className="grid grid-cols-[8rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2 border-y border-border bg-transparent px-3 py-2.5">
      {typeOptions.length > 0 && (
        <>
          <span className="pt-1 text-xs font-medium text-secondary">
            {captions.infringementType}
          </span>
          <div
            role="group"
            aria-label="Infringement type"
            className="flex min-w-0 flex-wrap gap-1.5"
          >
            {typeOptions.map((type) => (
              <FilterPill
                key={type}
                label={infringementTypeLabel(type)}
                active={activeTypes.has(type)}
                onClick={() => onToggleType(type)}
              />
            ))}
          </div>
        </>
      )}
      {statusOptions.length > 0 && (
        <>
          <span className="pt-1 text-xs font-medium text-secondary">{captions.responseStatus}</span>
          <div role="group" aria-label="Response status" className="flex min-w-0 flex-wrap gap-1.5">
            {statusOptions.map((status) => (
              <FilterPill
                key={status}
                label={titleCase(status)}
                active={activeStatuses.has(status)}
                tone={responseTone(status)}
                onClick={() => onToggleStatus(status)}
              />
            ))}
          </div>
        </>
      )}
      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="col-start-2 h-7 justify-self-end px-1 text-xs font-medium text-muted transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function InfringementRecordsComponent({
  data,
  config,
  dashboard,
  pagination,
}: WidgetProps<InfringementRecordsConfig>) {
  const labels = resolveLabels(LABELS, config);
  const filterCaptions = {
    infringementType: labels.filterInfringementType,
    responseStatus: labels.filterResponseStatus,
  };
  const error = firstError(data, ["records"]);
  const resolved = unwrap<InfringementRecordsData>(data.records);
  const isLoading = data.records?.isLoading ?? false;

  const title = config.title ?? defaultConfig.title ?? "Infringement Records";
  const interactive = typeof dashboard.emitInteraction === "function";
  const records = resolved?.records ?? EMPTY_RECORDS;
  const recordsPagination = pagination?.records;
  const activeInfringementId = dashboard.filters.dataFilters.find(
    (filter) => filter.field === "infringement_id",
  )?.value;

  const activeTypes = useMemo(
    () => getDashboardFilterValues(dashboard.filters, "infringement_type"),
    [dashboard.filters],
  );
  const normalizedActiveTypes = useMemo(
    () => new Set([...activeTypes].map(normalizeInfringementType)),
    [activeTypes],
  );
  const activeStatuses = useMemo(
    () =>
      new Set(
        [...getDashboardFilterValues(dashboard.filters, "latest_response_status")].map(
          normalizeResponseStatus,
        ),
      ),
    [dashboard.filters],
  );
  const activeSearchFilter = dashboard.filters.dataFilters.find(
    (filter) => filter.field === "search",
  )?.value;
  const activeSearch = typeof activeSearchFilter === "string" ? activeSearchFilter : "";
  const typeOptions = config.infringementTypes ?? defaultConfig.infringementTypes ?? [];
  const statusOptions = config.responseStatuses ?? defaultConfig.responseStatuses ?? [];
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterPanelId = useId();

  function toggleSet(set: Set<string>, value: string): Set<string> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  }

  function toggleFilter(filterKey: "infringementTypes" | "responseStatuses", value: string) {
    const currentValues = filterKey === "infringementTypes" ? activeTypes : activeStatuses;
    const nextValues = toggleSet(currentValues, value);

    if (nextValues.size === 0) {
      dashboard.clearFilter({ dataKey: "records", filterKey });
      return;
    }

    dashboard.applyFilter({
      dataKey: "records",
      filterKey,
      value: [...nextValues].sort(),
    });
  }

  function toggleType(type: string) {
    toggleFilter("infringementTypes", type);
  }

  function toggleStatus(status: string) {
    toggleFilter("responseStatuses", status);
  }

  function applySearch(value: string) {
    if (!value) {
      dashboard.clearFilter({ dataKey: "records", filterKey: "search" });
      return;
    }
    dashboard.applyFilter({ dataKey: "records", filterKey: "search", value });
  }

  function clearFilters() {
    dashboard.clearFilter({
      dataKey: "records",
      filterKey: "infringementTypes",
    });
    dashboard.clearFilter({
      dataKey: "records",
      filterKey: "responseStatuses",
    });
    dashboard.clearFilter({ dataKey: "records", filterKey: "search" });
  }

  // ── Filtered records ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (
        normalizedActiveTypes.size > 0 &&
        !normalizedActiveTypes.has(normalizeInfringementType(r.infringement_type))
      ) {
        return false;
      }
      if (activeStatuses.size > 0) {
        const status = normalizeResponseStatus(r.latest_response_status);
        if (!activeStatuses.has(status)) return false;
      }
      return true;
    });
  }, [records, normalizedActiveTypes, activeStatuses]);

  const handleSelect = (record: InfringementRecordEntry) => {
    dashboard.emitInteraction?.({
      type: "infringement.record.select",
      payload: {
        ...record,
        visible_records: filtered,
        source: "infringement-records",
      } as unknown as JsonValue,
      source: { dataKey: "records" },
    });
  };

  // ── Empty / error states ──────────────────────────────────────────────────

  if (error) {
    console.error("[InfringementRecordsWidget] Failed to load data:", error);
    return (
      <Widget title={title} className="min-h-48">
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  if (records.length === 0 && isLoading) {
    return (
      <Widget title={title} className="min-h-48">
        <div className="space-y-2 p-3.5">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="h-4 w-40 rounded bg-border/50" />
              <div className="h-4 w-24 rounded bg-border/40" />
              <div className="h-4 w-24 rounded bg-border/40" />
              <div className="h-4 w-14 rounded bg-border/40" />
              <div className="h-4 w-14 rounded bg-border/40" />
              <div className="h-4 flex-1 rounded bg-border/30" />
              <div className="h-4 w-24 rounded bg-border/40" />
            </div>
          ))}
        </div>
      </Widget>
    );
  }

  if (records.length === 0) {
    return (
      <Widget
        title={title}
        className="min-h-48"
        actions={
          <HeaderControls
            filtersExpanded={filtersExpanded}
            filterPanelId={filterPanelId}
            onFiltersExpandedChange={setFiltersExpanded}
            typeOptions={typeOptions}
            statusOptions={statusOptions}
            activeTypes={activeTypes}
            activeStatuses={activeStatuses}
            searchValue={activeSearch}
            onSearch={applySearch}
            searchPlaceholder={labels.searchPlaceholder}
          />
        }
      >
        <div className="flex h-full flex-col">
          {filtersExpanded && (
            <div id={filterPanelId}>
              <FilterRow
                typeOptions={typeOptions}
                statusOptions={statusOptions}
                activeTypes={activeTypes}
                activeStatuses={activeStatuses}
                onToggleType={toggleType}
                onToggleStatus={toggleStatus}
                onClear={clearFilters}
                captions={filterCaptions}
              />
            </div>
          )}
          <EmptyDataState />
        </div>
      </Widget>
    );
  }

  return (
    <Widget
      title={title}
      className="min-h-48"
      actions={
        <div className="flex items-center gap-2">
          {isLoading && <UpdatingPill />}
          <HeaderControls
            filtersExpanded={filtersExpanded}
            filterPanelId={filterPanelId}
            onFiltersExpandedChange={setFiltersExpanded}
            typeOptions={typeOptions}
            statusOptions={statusOptions}
            activeTypes={activeTypes}
            activeStatuses={activeStatuses}
            searchValue={activeSearch}
            onSearch={applySearch}
            searchPlaceholder={labels.searchPlaceholder}
          />
        </div>
      }
    >
      <div className="flex h-full flex-col">
        <div className="border-b border-border px-4 py-4">
          <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
            <div>
              <p className="mb-1 text-metric-label text-secondary">{labels.kpiShown}</p>
              <MetricValue>{filtered.length}</MetricValue>
            </div>
            <div>
              <p className="mb-1 text-metric-label text-secondary">{labels.kpiActionable}</p>
              <MetricValue
                state={
                  filtered.some((record) => record.is_actionable && record.response_count === 0)
                    ? "warn"
                    : "neutral"
                }
              >
                {
                  filtered.filter((record) => record.is_actionable && record.response_count === 0)
                    .length
                }
              </MetricValue>
            </div>
            <div>
              <p className="mb-1 text-metric-label text-secondary">{labels.kpiFailed}</p>
              <MetricValue
                state={
                  filtered.some((record) => record.failed_response_count > 0) ? "error" : "neutral"
                }
              >
                {filtered.filter((record) => record.failed_response_count > 0).length}
              </MetricValue>
            </div>
          </div>
          <p className="mt-2 text-metric-detail text-muted">
            Counts for the loaded records matching these filters
            {resolved?.has_more || recordsPagination?.hasNextPage
              ? " · More records available"
              : ""}
            .
          </p>
        </div>
        {filtersExpanded && (
          <div id={filterPanelId}>
            <FilterRow
              typeOptions={typeOptions}
              statusOptions={statusOptions}
              activeTypes={activeTypes}
              activeStatuses={activeStatuses}
              onToggleType={toggleType}
              onToggleStatus={toggleStatus}
              onClear={clearFilters}
              captions={filterCaptions}
            />
          </div>
        )}
        {/* ── Table ── */}
        <div className="min-h-0 flex-1 overflow-auto">
          {filtered.length === 0 ? (
            <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 p-4 text-center">
              <div>
                <WidgetState title={labels.noMatchTitle} description={labels.noMatchDescription} />
              </div>
              <Button size="sm" onClick={clearFilters}>
                {labels.clearFilters}
              </Button>
            </div>
          ) : (
            <table className="w-full min-w-[72.5rem] table-fixed border-collapse text-left text-sm">
              <colgroup>
                <col className="w-[26%]" />
                <col className="w-[18%]" />
                <col className="w-[14%]" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-32" />
                <col className="w-36" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-surface-elevated">
                <tr className="border-b border-border">
                  <th className="px-3 py-3 text-xs font-medium text-muted">{labels.colName}</th>
                  <th className="px-2 py-3 text-xs font-medium text-muted">
                    {labels.colEngineTenant}
                  </th>
                  <th className="px-2 py-3 text-xs font-medium text-muted">
                    {labels.colCrossDomain}
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted">
                    {labels.colDetections}
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted">
                    {labels.colResponses}
                  </th>
                  <th className="px-2 py-3 text-xs font-medium text-muted">{labels.colStatus}</th>
                  <th className="py-2 pl-2 pr-[1.625rem] text-right text-xs font-semibold uppercase tracking-wide text-muted">
                    {labels.colFirstSeen}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((record) => (
                  <InfringementRow
                    key={record.infringement_id}
                    record={record}
                    active={String(activeInfringementId ?? "") === record.infringement_id}
                    interactive={interactive}
                    onSelect={handleSelect}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        <WidgetFooter
          actions={
            recordsPagination?.hasNextPage ? (
              <Button
                size="sm"
                onClick={recordsPagination.loadMore}
                loading={recordsPagination.isLoadingMore}
                loadingLabel="Loading more…"
              >
                {labels.loadMore}
              </Button>
            ) : undefined
          }
        >
          <span>
            {filtered.length === records.length
              ? `${records.length} records loaded`
              : `${filtered.length} of ${records.length} loaded records`}
          </span>
        </WidgetFooter>
      </div>
    </Widget>
  );
}

export const Component = InfringementRecordsComponent;

// ── Detail drawer ─────────────────────────────────────────────────────────────

function responseStatusTone(status: string): "ok" | "warn" | "error" | "neutral" {
  return responseTone(status);
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(6rem,0.4fr)_minmax(0,1fr)] items-start gap-4 py-2.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-primary">{value}</dd>
    </div>
  );
}

function ResponseHistory({ record }: { record: InfringementRecordEntry }) {
  const timezone = useDisplayTimezone();
  if (record.responses.length === 0) {
    return (
      <p className="text-xs text-muted">
        No response recorded yet — use an action below or add an Activity note.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {record.responses.map((response) => (
        <li key={response.response_id} className="flex items-start justify-between gap-3 text-xs">
          <span className="min-w-0">
            <span className="flex min-w-0 items-center gap-2">
              <StatusChip
                status={responseStatusTone(response.response_status)}
                label={titleCase(response.response_status)}
              />
              <span className="truncate text-secondary">
                {response.response_type} · {response.execution_mode}
              </span>
            </span>
            <span className="mt-1 block text-muted">
              {response.engine_label ?? "No engine selected"}
              {formatResponseTargets(response.targets).map((target) => ` · ${target}`)}
            </span>
          </span>
          <span className="shrink-0 text-muted">{formatDate(response.trigger_date, timezone)}</span>
        </li>
      ))}
    </ul>
  );
}

type ActivityNote = { id: number; text: string; at: string };
type DetailView = "details" | "response" | "activity";
type ResponseAction = "block" | "notify" | "escalate";

const DETAIL_TABS: Array<{
  id: DetailView;
  label: string;
  icon: typeof FileText;
  iconClassName: string;
  activeClassName: string;
}> = [
  {
    id: "details",
    label: "Details",
    icon: FileText,
    iconClassName: "text-brand-cyan",
    activeClassName: "border-brand-cyan bg-brand-cyan/5",
  },
  {
    id: "response",
    label: "Response",
    icon: ShieldCheck,
    iconClassName: "text-brand-purple",
    activeClassName: "border-brand-purple bg-brand-purple/5",
  },
  {
    id: "activity",
    label: "Activity",
    icon: UserRoundCheck,
    iconClassName: "text-brand-navy dark:text-primary",
    activeClassName: "border-brand-navy/50 bg-surface-elevated dark:border-primary/50",
  },
];

const EXTRA_BADGE_LIMIT = 8;

function extraRows(extra: JsonValue | null): Array<{ label: string; values: string[] }> {
  if (extra === null || typeof extra !== "object" || Array.isArray(extra)) return [];

  return Object.entries(extra).map(([key, value]) => ({
    label: key.toLowerCase() === "ips" ? "IPs" : titleCase(key.replace(/_/g, " ")),
    values: Array.isArray(value)
      ? value.map((item) => String(item))
      : [value !== null && typeof value === "object" ? JSON.stringify(value) : String(value)],
  }));
}

function ExtraBadges({ badges }: { badges: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const visibleBadges = expanded ? badges : badges.slice(0, EXTRA_BADGE_LIMIT);
  const hiddenCount = badges.length - visibleBadges.length;

  if (badges.length === 0) return <span className="text-muted">—</span>;

  return (
    <div className="flex flex-wrap gap-1.5">
      {visibleBadges.map((badge, index) => (
        <span
          key={`${badge}-${index}`}
          className="max-w-full break-all rounded border border-border bg-surface-elevated px-2 py-0.5 text-xs text-secondary"
        >
          {badge}
        </span>
      ))}
      {badges.length > EXTRA_BADGE_LIMIT ? (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="rounded border border-brand-cyan/30 px-2 py-0.5 text-xs font-medium text-brand-cyan transition-colors hover:bg-brand-cyan/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          {expanded ? "Show less" : `+${hiddenCount} more`}
        </button>
      ) : null}
    </div>
  );
}

function InfringementActivity({
  record,
  onAddNote,
}: {
  record: InfringementRecordEntry;
  onAddNote: (text: string, timestamp: string) => void;
}) {
  const [notes, setNotes] = useState<ActivityNote[]>([]);
  const timezone = useDisplayTimezone();
  const [draft, setDraft] = useState("");

  const journalParams = new URLSearchParams();
  if (record.event_label) journalParams.set("event", record.event_label);
  if (record.channel ?? record.stream_label) {
    journalParams.set("channel", record.channel ?? record.stream_label ?? "");
  }
  const eventJournalHref = `/journal${journalParams.size > 0 ? `?${journalParams}` : ""}`;

  const send = (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    const timestamp = new Date().toISOString();
    setNotes((current) => [...current, { id: current.length + 1, text, at: timestamp }]);
    onAddNote(text, timestamp);
    setDraft("");
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border bg-surface-elevated px-6 py-4">
        <div>
          <h3 className="text-sm font-semibold text-primary">Infringement activity</h3>
          <p className="mt-1 text-xs text-muted">
            System updates and analyst notes linked to this infringement.
          </p>
        </div>
        <a
          href={eventJournalHref}
          target="_blank"
          rel="noreferrer"
          title="Open the event Journal in a new tab"
          className="inline-flex shrink-0 items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-secondary transition-colors hover:bg-surface-elevated hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          Event Journal
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <ol className="space-y-4">
          <li className="grid grid-cols-[1.5rem_1fr] gap-3">
            <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-brand-cyan/10 text-brand-cyan">
              <CircleAlert className="h-3.5 w-3.5" aria-hidden />
            </span>
            <div>
              <p className="text-sm font-medium text-primary">Infringement detected</p>
              <p className="mt-0.5 text-xs text-secondary">
                {record.engine_label} reported {record.detection_count} detection
                {record.detection_count === 1 ? "" : "s"}.
              </p>
              <p className="mt-1 text-xs text-muted">
                System · {formatDate(record.first_detection, timezone)}
              </p>
            </div>
          </li>
          {record.responses.map((response) => (
            <li key={response.response_id} className="grid grid-cols-[1.5rem_1fr] gap-3">
              <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-brand-purple/10 text-brand-purple">
                <Check className="h-3.5 w-3.5" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-medium text-primary">{response.response_type}</p>
                <p className="mt-0.5 text-xs text-secondary">
                  {titleCase(response.response_status)} · {titleCase(response.execution_mode)}
                </p>
                <p className="mt-1 text-xs text-muted">
                  System · {formatDate(response.trigger_date, timezone)}
                </p>
              </div>
            </li>
          ))}
          {notes.map((note) => (
            <li key={note.id} className="grid grid-cols-[1.5rem_1fr] gap-3">
              <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-surface-elevated text-secondary">
                <UserRoundCheck className="h-3.5 w-3.5" aria-hidden />
              </span>
              <div>
                <p className="whitespace-pre-wrap break-words text-sm text-primary">{note.text}</p>
                <p className="mt-1 text-xs text-muted">You · {formatDate(note.at, timezone)}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <form
        onSubmit={send}
        className="shrink-0 border-t border-border bg-surface-elevated px-6 py-4"
      >
        <label htmlFor="activity-note" className="mb-2 block text-xs font-medium text-secondary">
          Add analyst note
        </label>
        <textarea
          id="activity-note"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={2}
          placeholder="Record an assessment, decision, or handover note"
          className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-primary placeholder:text-muted focus:border-brand-cyan/60 focus:outline-none focus:ring-1 focus:ring-brand-cyan/30"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            disabled={draft.trim().length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-brand-cyan px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-cyan/90 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <Send className="h-4 w-4" />
            Add note
          </button>
        </div>
      </form>
    </div>
  );
}

function InfringementDetailBody({
  record,
  onResponseAction,
  onAddActivityNote,
}: {
  record: InfringementRecordEntry;
  onResponseAction: (action: ResponseAction, scope: "channel" | "all", timestamp: string) => void;
  onAddActivityNote: (text: string, timestamp: string) => void;
}) {
  const [view, setView] = useState<DetailView>("details");
  const timezone = useDisplayTimezone();
  const [pendingAction, setPendingAction] = useState<ResponseAction | null>(null);
  const [scope, setScope] = useState<"channel" | "all">("channel");
  const [confirmedAction, setConfirmedAction] = useState<{
    action: ResponseAction;
    label: string;
    scope: "channel" | "all";
    at: string;
  } | null>(null);

  const targets = recordTargets(record);
  const metadataRows = extraRows(record.extra);

  const latestStatus = record.latest_response_status;
  const isHandled = latestStatus?.toLowerCase() === "confirmed";
  const hasPendingResponse = record.pending_response_count > 0;
  const autoResponseLabel = isHandled
    ? "The system confirmed an automated response."
    : hasPendingResponse
      ? "An automated response is currently in progress."
      : record.response_count > 0
        ? "An automated response was recorded and needs review."
        : "No automated response was triggered — a manual response is available.";

  const actions: Array<{
    id: ResponseAction;
    label: string;
    description: string;
    icon: typeof ShieldBan;
    iconClassName: string;
  }> = [
    {
      id: "block",
      label: "Block targets",
      description: "Send detected targets for disruption.",
      icon: ShieldBan,
      iconClassName: "bg-brand-purple/10 text-brand-purple",
    },
    {
      id: "notify",
      label: "Notify partner",
      description: "Request action from the response partner.",
      icon: BellRing,
      iconClassName: "bg-brand-purple/10 text-brand-purple",
    },
    {
      id: "escalate",
      label: "Escalate for review",
      description: "Assign this infringement for analyst review.",
      icon: UserRoundCheck,
      iconClassName: "bg-brand-purple/10 text-brand-purple",
    },
  ];
  const selectedAction = actions.find((action) => action.id === pendingAction);

  return (
    <DetailWorkspace
      label="Infringement view"
      tabs={DETAIL_TABS.map((tab) => ({
        id: tab.id,
        label:
          tab.label +
          (tab.id === "response" && record.response_count > 0 ? ` (${record.response_count})` : ""),
      }))}
      activeTab={view}
      onTabChange={(id) => setView(id as DetailView)}
      actions={
        <>
          <Button size="sm" onClick={() => setView("activity")}>
            Activity & notes
          </Button>
          <Button size="sm" variant="primary" onClick={() => setView("response")}>
            Review response
          </Button>
        </>
      }
    >
      {view === "details" ? (
        <div className="h-full min-h-0 overflow-y-auto px-6 py-4">
          <h3 className="mb-2 text-xs font-semibold text-brand-cyan">Event and scope</h3>
          <dl className="divide-y divide-border/50 border-y border-border">
            <Row label="Protected event" value={record.event_label ?? "—"} />
            <Row label="Channel" value={record.channel ?? record.stream_label ?? "—"} />
            <Row
              label="Tenant"
              value={
                record.customer
                  ? `${record.tenant_label} · ${record.customer}`
                  : record.tenant_label
              }
            />
            <Row
              label="Targets"
              value={
                targets.length > 0 || record.pirate_endpoint_ids.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {targets.map((value) => (
                      <span
                        key={value}
                        className="rounded border border-border bg-surface-elevated px-2 py-0.5 text-xs text-secondary"
                      >
                        {value}
                      </span>
                    ))}
                    {record.pirate_endpoint_ids.length > 0 ? (
                      <span className="rounded border border-border bg-surface-elevated px-2 py-0.5 text-xs text-secondary">
                        {record.pirate_endpoint_ids.length} pirate endpoints
                      </span>
                    ) : null}
                  </div>
                ) : (
                  "—"
                )
              }
            />
          </dl>

          <h3 className="mb-2 mt-6 text-xs font-semibold text-data-detection">Detection</h3>
          <dl className="divide-y divide-border/50 border-y border-border">
            <Row label="Infringement" value={infringementTypeLabel(record.infringement_type)} />
            <Row label="Engine" value={record.engine_label} />
            <Row label="Domain" value={record.detection_domain} />
            <Row label="First detected" value={formatDate(record.first_detection, timezone)} />
          </dl>

          <h3 className="mb-2 mt-6 text-xs font-semibold text-brand-cyan">
            Identifiers and metadata
          </h3>
          <dl className="divide-y divide-border/50 border-y border-border">
            <Row
              label="Infringement ID"
              value={<span className="font-mono text-xs">{record.infringement_id}</span>}
            />
            <Row
              label="Engine infringement ID"
              value={<span className="font-mono text-xs">{record.engine_infringement_id}</span>}
            />
            {record.origin_engine_label || record.origin_engine_id !== null ? (
              <Row
                label="Cross Domain"
                value={record.origin_engine_label ?? record.origin_engine_id}
              />
            ) : null}
            {metadataRows.map(({ label, values }) => (
              <Row key={label} label={label} value={<ExtraBadges badges={values} />} />
            ))}
          </dl>
        </div>
      ) : view === "response" ? (
        <div className="h-full min-h-0 overflow-y-auto px-6 py-5">
          <div className="border-b border-border pb-5">
            <p className="text-xs font-semibold text-data-response">Current response</p>
            <p className="mt-2 text-sm text-secondary">{autoResponseLabel}</p>

            {record.is_actionable && !hasPendingResponse && !isHandled ? (
              <div className="mt-4 flex items-start gap-2.5 rounded-md border border-brand-purple/20 bg-brand-purple/5 px-3 py-2.5">
                <span className="mt-0.5 inline-flex shrink-0 items-center justify-center rounded-md bg-brand-purple/10 p-1 text-brand-purple">
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-primary">Manual response recommended</p>
                  <p className="mt-0.5 text-xs text-secondary">
                    No response is currently in progress. Choose one of the actions below if
                    intervention is required.
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="border-b border-border py-5">
            <h3 className="text-sm font-semibold text-brand-purple">Response actions</h3>
            <div className="mt-3 divide-y divide-border rounded-md border border-border">
              {actions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => setPendingAction(action.id)}
                    className="group flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${action.iconClassName}`}
                    >
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-primary">{action.label}</span>
                      <span className="block text-xs text-muted">{action.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {selectedAction ? (
              <div
                className="mt-3 rounded-md border border-brand-purple/25 bg-brand-purple/5 p-4"
                role="alert"
              >
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-purple/10 text-brand-purple">
                    <ShieldCheck className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-primary">
                      Confirm {selectedAction.label.toLowerCase()}
                    </p>
                    <p className="mt-1 text-xs text-secondary">
                      The request will be sent and recorded in the event Journal.
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex gap-4" role="radiogroup" aria-label="Action scope">
                  {(["channel", "all"] as const).map((option) => (
                    <label key={option} className="flex items-center gap-2 text-sm text-secondary">
                      <input
                        type="radio"
                        name="response-scope"
                        value={option}
                        checked={scope === option}
                        onChange={() => setScope(option)}
                        className="accent-brand-purple"
                      />
                      {option === "channel" ? "Current channel" : "All affected channels"}
                    </label>
                  ))}
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingAction(null)}
                    className="rounded px-3 py-1.5 text-sm font-medium text-secondary hover:bg-surface-elevated"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const timestamp = new Date().toISOString();
                      onResponseAction(selectedAction.id, scope, timestamp);
                      setConfirmedAction({
                        action: selectedAction.id,
                        label: selectedAction.label,
                        scope,
                        at: timestamp,
                      });
                      setPendingAction(null);
                    }}
                    className="rounded bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-purple/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/30"
                  >
                    Confirm action
                  </button>
                </div>
              </div>
            ) : confirmedAction ? (
              <div
                className="mt-3 flex items-start gap-2.5 rounded-md border border-[var(--status-ok)]/30 bg-[var(--status-ok-bg)] px-3 py-3"
                role="status"
                aria-live="polite"
              >
                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--status-ok)]/15 text-[var(--status-ok)]">
                  <Check className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-primary">Action request submitted</p>
                  <p className="mt-0.5 text-xs text-secondary">
                    {confirmedAction.label} ·{" "}
                    {confirmedAction.scope === "channel"
                      ? "Current channel"
                      : "All affected channels"}{" "}
                    · Added to the event Journal.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmedAction(null)}
                  className="shrink-0 rounded px-2 py-1 text-xs font-medium text-secondary hover:bg-[var(--status-ok)]/10"
                >
                  Dismiss
                </button>
              </div>
            ) : null}
          </div>

          <div className="pt-5">
            <h3 className="mb-3 text-sm font-semibold text-data-response">Response history</h3>
            <ResponseHistory record={record} />
          </div>
        </div>
      ) : (
        <div className="h-full min-h-0">
          <InfringementActivity record={record} onAddNote={onAddActivityNote} />
        </div>
      )}
    </DetailWorkspace>
  );
}

export function renderDetail(
  payload: JsonValue,
  callbacks: DrawerCallbacks,
): DrawerDescriptor | null {
  const record = payload as unknown as InfringementRecordEntry;
  if (typeof record?.infringement_id !== "string") return null;

  return {
    bodyLayout: "workspace",
    meta: (
      <dl className="grid grid-cols-3 gap-4">
        <div>
          <dt className="text-xs text-muted">Detections</dt>
          <dd className="mt-1 text-xl font-semibold tabular-nums text-primary">
            {record.detection_count.toLocaleString()}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Confidence</dt>
          <dd className="mt-1 text-xl font-semibold tabular-nums text-primary">
            {record.confidence_score.toFixed(2)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Last detected</dt>
          <dd className="mt-1 break-words text-xs text-secondary">
            <DateTimeText value={record.last_detection} />
          </dd>
        </div>
      </dl>
    ),
    title: record.infringement_name,
    subtitle: `${infringementTypeLabel(record.infringement_type)} · ${record.tenant_label}${record.customer ? ` · ${record.customer}` : ""}`,
    badges: record.latest_response_status ? (
      <ResponseStatusIndicator status={record.latest_response_status} />
    ) : (
      <span className="text-xs font-medium text-muted">No response</span>
    ),
    children: (
      <InfringementDetailBody
        key={record.infringement_id}
        record={record}
        onResponseAction={(action, scope, timestamp) => {
          callbacks.onInteraction("infringement.response.request", {
            infringement_id: record.infringement_id,
            action,
            scope,
            channel: scope === "channel" ? (record.channel ?? record.stream_label ?? null) : null,
            targets: recordTargets(record),
          });

          callbacks.onInteraction("infringement.activity.note.add", {
            infringement_id: record.infringement_id,
            infringement_name: record.infringement_name,
            protected_event_id: record.protected_event_ids[0] ?? null,
            event_label: record.event_label ?? null,
            channel: record.channel ?? record.stream_label ?? null,
            tenant_id: record.tenant_id,
            tenant_label: record.tenant_label,
            text: `Response action requested: ${action === "block" ? "Block targets" : action === "notify" ? "Notify partner" : "Escalate for review"} (${scope === "channel" ? "current channel" : "all affected channels"}).`,
            timestamp,
            source: "response_action",
          });
        }}
        onAddActivityNote={(text, timestamp) =>
          callbacks.onInteraction("infringement.activity.note.add", {
            infringement_id: record.infringement_id,
            infringement_name: record.infringement_name,
            protected_event_id: record.protected_event_ids[0] ?? null,
            event_label: record.event_label ?? null,
            channel: record.channel ?? record.stream_label ?? null,
            tenant_id: record.tenant_id,
            tenant_label: record.tenant_label,
            text,
            timestamp,
          })
        }
      />
    ),
    defaultWidthVw: 42,
  };
}

const widgetModule: WidgetModule<InfringementRecordsConfig> = {
  manifest,
  Component,
  defaultConfig,
  renderDetail,
};

export default widgetModule;
